import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';

import { BetSlipType } from '@bpos';
import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { PlaceBetResponse, PlaceBetStatus, PlacementErrorType } from '@frontend/sports/types/betslip';
import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';
import { UserActions } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { flatten, uniqBy } from 'lodash-es';
import { EventBridgeService } from 'packages/sports/web/app/src/event-bridge/event-bridge.service';
import { GeolocationCheckService } from 'packages/sports/web/app/src/geo-location/geo-location-check.service';
import {
    Observable,
    catchError,
    combineLatest,
    concatMap,
    delay,
    exhaustMap,
    filter,
    from,
    interval,
    map,
    of,
    startWith,
    switchMap,
    takeUntil,
} from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipHost } from '../../../core/external-betslip-actions';
import { OverAskService } from '../../over-ask/services/over-ask.service';
import { QuickBetActions } from '../../quick-bet/quick-bet-actions';
import { QuickDepositActions } from '../../quick-deposit/quick-deposit-actions';
import BetslipTrackingActions from '../../tracking/state/tracking.actions';
import { mapSlipToTrackingSlip } from '../../tracking/tracking.utils';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { TechnicalError } from '../../validation/errors/general/technical-error';
import { GeoLocationError } from '../../validation/errors/geo-location/geo-location-error';
import { NotLoggedInError } from '../../validation/errors/user/not-logged-in-error';
import { emptyPickErrors } from '../../validation/state';
import { BetslipPlaceBetActions, PlaceBetFailedReason, QueryPlaceStatusPayload } from '../actions';
import { IFailedSlipResult, IPlacedBetslipResult } from '../models';
import BetBuilderPlaceBetService from '../services/bet-builder-place-bet-service';
import PlaceBetResponseProcessorService from '../services/place-bet-response-processor.service';
import PlaceBetService from '../services/place-bet.service';
import RequestIdBuilder from '../services/request-id-builder';
import PlaceBetRequestBuilder from '../services/request/place-bet-request-builder';
import { PlaceButtonMode } from '../state';
import { isPartialSlip, isSucceededNonPartialSlip } from '../utils';

@Injectable()
export default class PlaceBetEffects {
    private readonly logger: SportsRemoteLogger;

    constructor(
        private actions$: Actions,
        private store: Store,
        private placeBetService: PlaceBetService,
        private requestIdBuilder: RequestIdBuilder,
        private requestBuilder: PlaceBetRequestBuilder,
        private responseProcessor: PlaceBetResponseProcessorService,
        private overAskService: OverAskService,
        @Optional() private geolocationService: GeolocationCheckService | null,
        private betslipHost: BetslipHost,
        private betBuilderPlaceBetService: BetBuilderPlaceBetService,
        private balanceService: AccountBalanceService,
        loggerFactory: LoggerFactory,
        private eventBridgeService: EventBridgeService,
        private betslipConfig: BetslipConfig,
    ) {
        this.logger = loggerFactory.getLogger('PlaceBetEffects');
    }

    handlePlaceBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.requestPlaceBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            filter(
                ([action, state]) =>
                    state.types.base.currentSelectedType !== BetslipType.Teaser || state.base.displayMode === BetslipDisplayMode.Linear,
            ),
            exhaustMap(([action, state]) => {
                //	let geoCheck = this.geolocationService ? this.geolocationService.checkGeoLocation$({ waitForLocationAvailable: true }) : of(false);
                const geoCheck =
                    this.betslipHost === BetslipHost.Digital
                        ? this.geolocationService!.checkGeoLocation$({ waitForLocationAvailable: true })
                        : of(false);

                return combineLatest([geoCheck, this.betBuilderPlaceBetService.updateBetBuilderPicks(state, true)]).pipe(
                    switchMap(([isLocationFailed, _]) =>
                        isLocationFailed ? this.processError(new GeoLocationError(), '') : of(BetslipPlaceBetActions.placeBet()),
                    ),
                    catchError((error: HttpErrorResponse) => this.processError(error, '')),
                    startWith(
                        BetslipActions.setState({ state: BetslipState.Placing }),
                        BetslipPlaceBetActions.startedPlaceBet(),
                        BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Processing }),
                    ),
                );
            }),
        );
    });

    handlePlaceBetStartEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.placeBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            exhaustMap(([_, state]) => {
                const requestId = this.requestIdBuilder.generate();
                const { request, requestPicks, trackBetContext, trackBetContextV2, slips } =
                    state.base.displayMode === BetslipDisplayMode.Linear
                        ? this.requestBuilder.buildFromAllTypes(state, requestId)
                        : this.requestBuilder.build(state, requestId);
                const balanceBeforePlacement = this.balanceService.currentBalanceProperties?.accountBalance!;

                const sameBetslipType = request.betSlips.every(
                    (s) => s.betSlipType === request.betSlips[0].betSlipType && s.betGroups.length === request.betSlips[0].betGroups.length,
                );

                // TODO unit tests
                const hasMultipleSystemSlips =
                    uniqBy(
                        request.betSlips.filter((bs) => bs.betSlipType === BetSlipType.System),
                        (bs) => bs.systemSlipDetails.systemType,
                    ).length > 1;

                // in linear its possible to place multiple systems (trixie + 2Of3) at once, but overask is not enabled for those
                if (sameBetslipType && !hasMultipleSystemSlips) {
                    this.overAskService.initModern(state, requestPicks, request.betSlips[0]);
                }

                return this.placeBetService.placeBet(request, trackBetContext, trackBetContextV2).pipe(
                    concatMap((response) => {
                        const context: QueryPlaceStatusPayload = { request, requestPicks, state, trackBetContext, trackBetContextV2, slips };

                        this.eventBridgeService.notifySuccessfulBetPlacements(
                            {
                                betType: request.betSlips[0].betSlipType,
                                stake: request.betSlips[0].stake.amount,
                                totalPickCount: request.betSlips[0].bets.flatMap((bet) => bet.picks).length,
                                isEachWaySelected: undefined,
                                picks: request.betSlips[0].bets.flatMap((bet) => bet.picks),
                            },
                            request.betSlips[0].stake.currency,
                        );

                        return this.processResponse(response, context, balanceBeforePlacement);
                    }),
                    catchError((error: HttpErrorResponse) => this.processError(error, requestId)),
                    startWith(
                        // BetslipPlaceBetActions.saveBetInProgress({ payload: { request, requestPicks, trackBetContext, balanceBeforePlacement } }),
                        BetslipTrackingActions.checkoutSlip({ requestId, slips: slips.map(mapSlipToTrackingSlip) }),
                    ),
                );
            }),
        );
    });

    handleQueryStatusEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.queryPlaceStatus),
            switchMap((action) => {
                return this.placeBetService.queryStatus(action.request.requestId).pipe(
                    concatMap((response) => {
                        const context: QueryPlaceStatusPayload = {
                            request: action.request,
                            requestPicks: action.requestPicks,
                            state: action.state,
                            trackBetContext: action.trackBetContext,
                            trackBetContextV2: action.trackBetContextV2,
                            slips: action.slips,
                        };

                        return this.processResponse(response, context, action.balanceAtPlacement);
                    }),
                    catchError((error: HttpErrorResponse) => this.processError(error, action.request.requestId)),
                );
            }),
        );
    });

    //Clean errors automatically if it wasn't triggered by any other state change within 60 seconds
    handleAutoErrorCleanup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.placeBetFailed, BetslipErrorsActions.addBetslipError, QuickBetActions.placeBetResponseErrors),
            switchMap((_) => interval(60000).pipe(takeUntil(this.actions$.pipe(ofType(BetslipErrorsActions.cleanManualRemoveErrors))))),
            map((t) => BetslipErrorsActions.cleanManualRemoveErrors()),
        );
    });

    handleUserLoggedOutEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.userLoggedOut),
            map(() => BetslipActions.setState({ state: BetslipState.Edit })),
        );
    });

    handleQuickDepositCloseEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickDepositActions.quickDepositClose),
            map(() => BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Normal })),
        );
    });

    private processError(error: HttpErrorResponse | TechnicalError | GeoLocationError, requestId: string): Observable<Action> {
        const betPlacementErrors: BetslipError[] = [];
        let errorMessage = '';
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            betPlacementErrors.push(new NotLoggedInError());
            errorMessage = `Place bet failed with not logged in error ${requestId}`;
        } else {
            betPlacementErrors.push(new TechnicalError());
            errorMessage = `Place bet failed with technical error -${requestId}`;
        }
        this.logger.error(error, errorMessage);

        const actions: Action[] = [
            BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.TechnicalError, errorMessage }),
            BetslipErrorsActions.placeBetResponseErrors({
                pickErrors: emptyPickErrors(),
                betslipErrors: betPlacementErrors,
                slipErrors: {},
            }),
            BetslipActions.setState({ state: BetslipState.Edit }),
            BetslipPlaceBetActions.removeBetInProgress(),
            BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Normal }),
        ];

        if (error instanceof GeoLocationError) {
            return from(actions);
        }

        if (this.betslipConfig.noWaitingBalanceRefresh) {
            this.balanceService.refresh();

            return from(actions);
        }

        // Wait for refresh balance before we continue with the actions
        return this.balanceService.refresh$(false).pipe(
            catchError((_) => {
                this.logger.error(`Balance refresh failed post bet placement for ${requestId}`);

                return of(null);
            }),
            switchMap((_) => actions),
        );
    }

    private processResponse(
        response: HttpResponse<PlaceBetResponse>,
        context: QueryPlaceStatusPayload,
        balanceBeforePlacement: number,
    ): Observable<Action> {
        if (response.status !== 200 || !response.body) {
            return this.processError(new TechnicalError(), context.request.requestId);
        }
        const content = response.body;
        const status = this.responseProcessor.getStatus(content);
        if (status === PlaceBetStatus.Pending) {
            const pendingResult = this.responseProcessor.processPending(content);

            return this.processPlaceBetPendingResponse(pendingResult, context, balanceBeforePlacement);
        } else {
            const result = this.responseProcessor.processDone(content, context);
            if (result.status === PlaceBetStatus.Failed) {
                const slips = result.slips as unknown as IFailedSlipResult[];
                const slipErrors = flatten(slips.map((slip) => slip.errors));
                if (
                    slipErrors.find((error) =>
                        [
                            PlacementErrorType.ParallelGamePlayPrevention,
                            PlacementErrorType.PlayerNotRegisteredWithCFR,
                            PlacementErrorType.UserIsSelfExcluded,
                            PlacementErrorType.OasisDown,
                        ].includes(error.type),
                    )
                ) {
                    this.store.dispatch(BetslipPlaceBetActions.validatePlaceButton({ isValid: false }));
                }
            }

            return this.processDonePlacement(result, context, balanceBeforePlacement);
        }
    }

    private processPlaceBetPendingResponse(
        pendingResult: { delay: number },
        context: QueryPlaceStatusPayload,
        balanceBeforePlacement: number,
    ): Observable<Action> {
        return of(
            BetslipPlaceBetActions.queryPlaceStatus({
                ...context,
                balanceAtPlacement: balanceBeforePlacement,
            }),
            BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Processing }),
        ).pipe(delay(pendingResult.delay));
    }

    private processDonePlacement(
        result: IPlacedBetslipResult,
        context: QueryPlaceStatusPayload,
        balanceBeforePlacement?: number,
    ): Observable<Action> {
        const responseActionPayload = {
            request: context.request,
            requestPicks: context.requestPicks,
            placeBetResult: result,
            state: context.state,
        };

        const responseActions: Action[] = [
            BetslipPlaceBetActions.finishPlaceBet(responseActionPayload),
            BetslipPlaceBetActions.removeBetInProgress(),
            BetslipPlaceBetActions.setMode({ mode: PlaceButtonMode.Normal }),
        ];

        if (result.status !== PlaceBetStatus.Success) {
            return from(responseActions);
        }

        const balanceShouldChange = result.slips.some((slip) => isPartialSlip(slip) || (isSucceededNonPartialSlip(slip) && !slip.isFreebet));

        if (this.betslipConfig.noWaitingBalanceRefresh) {
            this.balanceService.refresh();
            responseActions.push(BetslipPlaceBetActions.trackBetPlacementResult(responseActionPayload));

            return from(responseActions);
        }

        return this.balanceService.refresh$(balanceShouldChange, balanceBeforePlacement).pipe(
            //catch errors in case the refresh wasn't successful
            //we still need the show the user the placement response
            catchError((_) => {
                this.logger.error(`Balance refresh failed post bet placement for ${context.request.requestId}`);

                return of(null);
            }),
            switchMap((_) => from(responseActions)),
            startWith(BetslipPlaceBetActions.trackBetPlacementResult(responseActionPayload)),
        );
    }
}
